import { Component, effect, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { MatTree } from '@angular/material/tree';
import { BucketService, FolderNode } from 'src/app/services/bucket/bucket.service';
import { BucketStoreService } from '../../bucket-store.service';

@Component({
  selector: 'app-folder-tree',
  standalone: false,
  templateUrl: './folder-tree.component.html',
  styleUrls: ['./folder-tree.component.scss'],
})

/**
 * Componente che gestisce una struttura ad albero di cartelle e file.
 * Permette di selezionare cartelle e di emettere i file contenuti in esse.
 */
export class FolderTreeComponent {
  private readonly _store = inject(BucketStoreService);

  @ViewChild('tree') tree!: MatTree<FolderNode, FolderNode>;

  /**
   * Evento emesso quando viene selezionata un folder.
   * Emette un array contenente i nomi dei file presenti nel folder selezionato.
   * Per poi visualizzarli sul componente (app-lista-file-bucket)
   */
  @Output() folderSelectedEvent = new EventEmitter<FolderNode>();

  get listaFolders() {
    return this._store.tree()[0]?.folders ?? [];
  }
  get aziendaFolder(): FolderNode | undefined {
    return (this._store.tree()[0]?.folders[0] as FolderNode) ?? undefined;
  }

  expandCollapseEffect = effect(() => {
    const folderSelected = this._store.selectedFolder();
    if (folderSelected && !this.tree?.isExpanded(folderSelected)) {
      this.tree?.expand(folderSelected);
    }
  });

  isHomeFolder = (idAzienda: string) => {
    if (!this.aziendaFolder) {
      return false;
    }
    return this.aziendaFolder.id === idAzienda;
  };

  folderIcon = (folder: FolderNode) => {
    if (this.isHomeFolder(folder.id)) {
      return 'home';
    } else if (this.hasSottoCartelle(folder) || this.hasFile(folder)) {
      return 'folder';
    } else {
      return 'folder_open';
    }
  };

  /**
   * Lista delle cartelle da visualizzare nell'albero.
   */

  /**
   * Funzione per ottenere i figli di tipo "folder" di un nodo.
   * @param node Nodo da cui ottenere i sotto-nodi
   * @returns Array di nodi di tipo "folder"
   */
  childrenAccessor = (node: FolderNode) => node.folders ?? [];

  /**
   * Funzione per determinare se un nodo (folder) ha dei sotto-nodi (sotto-folder).
   * @param node folder da controllare
   * @returns true se il nodo ha almeno una sotto-folder, false altrimenti
   */
  hasSottoCartelle = (node: FolderNode) => !!node.folders.length;
  hasFile = (node: FolderNode) => !!node.files.length;

  constructor(public readonly _bucketService: BucketService) {}

  /**
   * Gestisce il click su un nodo dell'albero.
   * Salva il nodo selezionato, emette i file se il nodo è un folder
   * e gestisce l'espansione/collasso delle sotto-cartelle.
   *
   * @param folder Nodo cliccato
   * @param tree Istanza dell'albero (MatTree) su cui eseguire operazioni
   */
  onFolderClick(folder: FolderNode) {
    this._store.setSelectedFolder(folder);
  }

  nuovoFolder(parentFolder: FolderNode, isCreazione: boolean) {
    this._store.creaFolder(parentFolder, isCreazione).subscribe((esito) => {
      console.log(this.listaFolders);

      if (esito?.id && isCreazione && this.tree) {
        this.tree?.expand(parentFolder);
      }
    });
  }

  folderCreato(isCreato: boolean, parentFolder: FolderNode) {
    if (this.tree && isCreato) {
      this.tree?.expand(parentFolder);
    }
  }

  public expandCollapseFolder(folder: FolderNode) {
    if (this.hasSottoCartelle(folder)) {
      if (this.tree.isExpanded(folder)) {
        this.tree.collapse(folder);
      } else {
        this.tree?.expand(folder);
      }
    }
  }

  isSelected(folder: FolderNode) {
    return folder === this._store.selectedFolder();
  }
}
